<template>
  <v-navigation-drawer
    v-model="drawer"
    class="sidebar"
    :class="{
      'sidebar--expanded': navigationExpanded,
      'sidebar--mobile': isMobile,
      'sidebar--pinned': isPinned,
      'sidebar--touch': isTouchCapable,
    }"
    :style="sheetStyle"
    fixed
    dark
    :tag="isMobile ? 'div' : 'nav'"
    :role="isMobile ? 'dialog' : null"
    :aria-modal="isMobile && drawer ? 'true' : null"
    :aria-label="isMobile ? 'Navigation' : 'Hauptnavigation'"
    :temporary="isMobile"
    :width="effectiveDrawerWidth"
    :mobile-breakpoint="960"
    overlay-color="#08090b"
    :overlay-opacity="0.62"
    :disable-resize-watcher="true"
    :disable-route-watcher="true"
    @mouseenter.native="handlePointerEnter"
    @mouseleave.native="handlePointerLeave"
    @mousedown.native.capture="handleNavigationPointerInteraction"
    @touchstart.native.capture="handleNavigationTouchStart"
    @touchend.native.capture="scheduleTouchRevealReset"
    @touchcancel.native.capture="clearTouchRevealClick"
    @click.native.capture="handleNavigationClickCapture"
    @focusin.native="handleFocusIn"
    @focusout.native="handleFocusOut"
    @keydown.tab.native="trapFocus">
    <div class="sidebar__ambient" aria-hidden="true"></div>

    <div class="sidebar__shell">
      <header class="sidebar__header">
        <button
          type="button"
          class="sidebar__brand-mark"
          :aria-label="isMobile ? 'Zur Dashboard-Startseite' : isPinned ? 'Automatisch einklappen' : 'Navigation offen halten'"
          :title="isMobile ? 'Zur Dashboard-Startseite' : isPinned ? 'Automatisch einklappen' : 'Navigation offen halten'"
          @click="handleBrandAction">
          <v-icon class="sidebar__brand-glyph" size="22" aria-hidden="true">mdi-view-dashboard-outline</v-icon>
          <v-icon v-if="!isMobile" class="sidebar__brand-action" size="12" aria-hidden="true">
            {{ isPinned ? "mdi-chevron-left" : "mdi-chevron-right" }}
          </v-icon>
        </button>

        <div class="sidebar__brand-copy sidebar__reveal">
          <strong>Dashboard<span>.</span></strong>
          <small>Control Center</small>
        </div>

        <v-btn
          v-if="isMobile"
          ref="closeButton"
          class="sidebar__header-action"
          icon
          aria-label="Navigation schließen"
          title="Navigation schließen"
          @click="closeDrawer">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
        <v-btn
          v-else
          class="sidebar__header-action sidebar__reveal"
          icon
          :aria-pressed="isPinned"
          :aria-label="isPinned ? 'Automatisch einklappen' : 'Navigation offen halten'"
          :title="isPinned ? 'Automatisch einklappen' : 'Navigation offen halten'"
          @click="togglePinned">
          <v-icon size="18">{{ isPinned ? "mdi-pin-off-outline" : "mdi-pin-outline" }}</v-icon>
        </v-btn>
      </header>

      <div class="sidebar__search sidebar__reveal">
        <v-text-field
          ref="navSearch"
          v-model="searchQuery"
          class="sidebar__search-field"
          solo
          flat
          dense
          rounded
          clearable
          hide-details
          autocomplete="off"
          aria-label="Navigation durchsuchen"
          placeholder="Navigation durchsuchen"
          prepend-inner-icon="mdi-magnify"
          clear-icon="mdi-close-circle-outline">
          <template v-slot:append>
            <kbd class="sidebar__shortcut" aria-hidden="true">/</kbd>
          </template>
        </v-text-field>
      </div>

      <div class="sidebar__navigation" :role="isMobile ? 'navigation' : null" :aria-label="isMobile ? 'Hauptnavigation' : null">
        <div class="sidebar__scroll">
          <section v-if="visibleQuickLinks.length" class="sidebar__section" aria-labelledby="quicklinks-title">
            <p id="quicklinks-title" class="sidebar__section-label sidebar__reveal">Schnellzugriff</p>
            <v-list class="sidebar__list" nav dense>
              <v-list-item
                v-for="shortcut in visibleQuickLinks"
                :key="shortcut.text"
                :to="shortcut.to"
                class="sidebar__item sidebar__quick-item"
                :class="{ 'sidebar__item--active': isPathActive(shortcut.to) }"
                :aria-current="isPathActive(shortcut.to) ? 'page' : null"
                :title="navigationExpanded ? null : shortcut.text"
                :ripple="!isTouchCapable"
                link
                @click="closeAfterNavigation">
                <v-list-item-icon class="sidebar__item-icon">
                  <v-icon size="20">{{ navigationIcon(shortcut) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content class="sidebar__item-copy">
                  <v-list-item-title>{{ shortcut.text }}</v-list-item-title>
                </v-list-item-content>
                <span class="sidebar__quick-dot" aria-hidden="true"></span>
              </v-list-item>
            </v-list>
          </section>

          <v-divider v-if="visibleQuickLinks.length" class="sidebar__divider"></v-divider>

          <section v-if="navigationItems.length || !visibleQuickLinks.length" class="sidebar__section" aria-labelledby="navigation-title">
            <p id="navigation-title" class="sidebar__section-label sidebar__reveal">
              {{ searchQuery ? "Suchergebnisse" : "Bereiche" }}
            </p>

            <v-list v-if="navigationItems.length" class="sidebar__list" nav dense :expand="Boolean(searchQuery)">
              <div v-for="item in navigationItems" :key="item.name || item.text" class="sidebar__entry">
                <v-list-group
                  v-if="item.subItems"
                  no-action
                  class="sidebar__group"
                  :class="{ 'sidebar__group--active': isItemActive(item) }"
                  :style="{ '--sidebar-group-height': `${item.subItems.length * 54 + 20}px` }"
                  :value="isGroupExpanded(item)"
                  :ripple="!isTouchCapable"
                  @change="handleGroupToggle(item, $event)"
                  @touchstart.native.capture="expandGroupForTouch">
                  <template v-slot:activator>
                    <v-list-item-icon class="sidebar__item-icon">
                      <v-icon size="20">{{ navigationIcon(item) }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="sidebar__item-copy">
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                    </v-list-item-content>
                  </template>

                  <v-list-item
                    v-for="subItem in item.subItems"
                    :key="subItem.name || subItem.text"
                    :to="subItem.name ? { name: subItem.name } : undefined"
                    class="sidebar__subitem"
                    :class="{ 'sidebar__subitem--active': isItemActive(subItem, item) }"
                    :aria-current="isItemActive(subItem, item) ? 'page' : null"
                    :ripple="!isTouchCapable"
                    link
                    @click="handleItemSelection(subItem, item)">
                    <v-list-item-icon class="sidebar__subitem-icon">
                      <v-icon size="17">{{ navigationIcon(subItem, item.icon) }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="sidebar__subitem-copy">
                      <v-list-item-title>{{ subItem.text }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-group>

                <v-list-item
                  v-else
                  :to="item.name ? { name: item.name } : undefined"
                  :exact="Boolean(item.exact)"
                  class="sidebar__item"
                  :class="{ 'sidebar__item--active': isItemActive(item) }"
                  :aria-current="isItemActive(item) ? 'page' : null"
                  :title="navigationExpanded ? null : item.text"
                  :ripple="!isTouchCapable"
                  link
                  @click="handleItemSelection(item)">
                  <v-list-item-icon class="sidebar__item-icon">
                    <v-icon size="20">{{ navigationIcon(item) }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="sidebar__item-copy">
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </div>
            </v-list>

            <div v-else-if="!visibleQuickLinks.length" class="sidebar__empty sidebar__reveal" role="status">
              <span class="sidebar__empty-icon"><v-icon size="20">mdi-magnify-close</v-icon></span>
              <strong>Nichts gefunden</strong>
              <small>Versuche einen anderen Begriff.</small>
            </div>
          </section>
        </div>
      </div>

      <footer v-if="utilityItems.length" class="sidebar__footer">
        <v-divider class="sidebar__divider"></v-divider>
        <v-list class="sidebar__list sidebar__utility-list" nav dense>
          <v-list-item
            v-for="item in utilityItems"
            :key="item.text"
            class="sidebar__item sidebar__utility-item"
            :title="navigationExpanded ? null : item.text"
            :ripple="!isTouchCapable"
            link
            @click="handleItemSelection(item)">
            <v-list-item-icon class="sidebar__item-icon">
              <v-icon size="20">{{ navigationIcon(item) }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content class="sidebar__item-copy">
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item-content>
            <v-icon class="sidebar__utility-arrow sidebar__reveal" size="16">mdi-arrow-top-right</v-icon>
          </v-list-item>
        </v-list>
      </footer>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapActions } from "vuex"
import userService from "../../../services/userService"

const DESKTOP_PIN_STORAGE_KEY = "roody-sidebar-pinned"
const TOUCH_PIN_STORAGE_KEY = "roody-sidebar-pinned-touch"

function getPinPreferenceContext() {
  const isTouchCapable = typeof navigator !== "undefined" && Number(navigator.maxTouchPoints || navigator.msMaxTouchPoints || 0) > 0

  return {
    isTouchCapable,
    storageKey: isTouchCapable ? TOUCH_PIN_STORAGE_KEY : DESKTOP_PIN_STORAGE_KEY,
    defaultPinned: !isTouchCapable,
  }
}

export default {
  props: {
    drawerstate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userService,
      drawer: false,
      railWidth: 76,
      searchQuery: "",
      expandedGroups: [],
      activeNavigationGroup: null,
      activeNavigationRouteName: null,
      pinStorageKey: DESKTOP_PIN_STORAGE_KEY,
      isTouchCapable: false,
      isPinned: false,
      isHovering: false,
      isTouchExpanded: false,
      suppressNextNavigationClick: false,
      hasNavigationFocus: false,
      keyboardNavigationActive: false,
      canHover: false,
      pointerQuery: null,
      hoverCloseTimer: null,
      focusCloseTimer: null,
      touchRevealTimer: null,
      previouslyFocusedElement: null,
      inertedElements: [],
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    isPhone() {
      return this.$vuetify.breakpoint.xsOnly
    },
    isDrawerControlled() {
      return Boolean(this.$listeners && this.$listeners["update:drawerstate"])
    },
    drawerWidth() {
      if (!this.isMobile) return 296

      const viewportWidth = this.$vuetify.breakpoint.width || (typeof window !== "undefined" ? window.innerWidth : 360)
      const maximumWidth = this.isPhone ? 328 : 340
      return Math.max(240, Math.min(maximumWidth, viewportWidth - 16))
    },
    effectiveDrawerWidth() {
      return !this.isMobile && !this.navigationExpanded ? this.railWidth : this.drawerWidth
    },
    reservedLayoutWidth() {
      if (this.isMobile) return 0
      return this.isPinned ? this.drawerWidth : this.railWidth
    },
    navigationExpanded() {
      return this.isMobile || this.isPinned || this.isHovering || this.isTouchExpanded || this.hasNavigationFocus
    },
    sheetStyle() {
      const style = {}
      const themeService = this.$vuetify && this.$vuetify.theme
      const currentTheme =
        themeService &&
        (themeService.currentTheme || (themeService.themes && (themeService.dark ? themeService.themes.dark : themeService.themes.light)))

      if (currentTheme && typeof currentTheme.primary === "string") style["--sidebar-accent"] = currentTheme.primary
      return style
    },
    quickLinks() {
      const email = this.userService.getEmail()

      return [
        {
          icon: "mdi-email-outline",
          text: "MAIL",
          to: "/ai/mail",
          visible: this.userService.hasRight("ai_mail"),
        },
        {
          icon: "mdi-open-in-new",
          text: "TAC",
          to: "/dashboard/tac/member-manager",
          visible: email === "dominic.ammann.ch@gmail.com" || email === "sebs@wellcomefit.ch",
        },
        {
          icon: "mdi-robot-happy-outline",
          text: "AI CHAT",
          to: "/ai/chat",
          visible: email === "dominic.ammann.ch@gmail.com" || email === "sebs@wellcomefit.ch" || email === "tk@wellcomefit.ch",
        },
        {
          icon: "mdi-school-outline",
          text: "Akademie",
          to: "/akademie",
          visible: this.userService.hasRight("aka_manage", "aka_teacher", "aka_fetch", "aka_leadership", "aka_leadership_manage"),
        },
        {
          icon: "mdi-calendar",
          text: "Kalender",
          to: "/work-calendar",
          visible: this.userService.hasRight("c_create", "wc_view_all_studios"),
        },
        {
          icon: "mdi-format-list-checks",
          text: "ToDos",
          to: "/dashboard/todo",
          visible: this.userService.hasRight("todo_fetchAndEdit", "oma_toDoListItems"),
        },
      ].filter((item) => item.visible)
    },
    visibleQuickLinks() {
      const query = (this.searchQuery || "").trim().toLocaleLowerCase("de-CH")
      if (!query) return this.quickLinks

      return this.quickLinks.filter((item) => `${item.text} ${item.to}`.toLocaleLowerCase("de-CH").includes(query))
    },
    allowedItems() {
      let user = null

      if (typeof localStorage !== "undefined") {
        try {
          const storedUser = localStorage.getItem("user")
          user = storedUser ? JSON.parse(storedUser) : null
        } catch (error) {
          user = null
        }
      }

      let items = [
        {
          icon: "mdi-calculator-variant-outline",
          text: "Buchhaltung",
          rights: ["accounting_alibeyBills"],
          subItems: [
            {
              text: "Alibey",
              icon: "mdi-airplane",
              name: "accountingAlibeyBills",
              rights: ["accounting_alibeyBills"],
            },
          ],
        },
        {
          icon: "mdi-view-dashboard",
          text: "Home",
          name: "dashboardHome",
          exact: true,
        },
        {
          icon: "mdi-cash-register",
          text: "Kasse",
          name: "posWorkspace",
          rights: ["cassa_admin"],
          subItems: [
            {
              text: "Verkauf",
              icon: "mdi-cart-outline",
              name: "posSale",
              rights: ["cassa_admin"],
            },
            {
              text: "Gutscheine",
              icon: "mdi-ticket-percent-outline",
              name: "posVouchers",
              rights: ["cassa_admin"],
            },
            {
              text: "Vorgänge",
              icon: "mdi-receipt-text-outline",
              name: "posReceipts",
              rights: ["cassa_admin"],
            },
            {
              text: "Übersicht",
              icon: "mdi-chart-box-outline",
              name: "posReports",
              rights: ["cassa_admin"],
            },
            {
              text: "Artikel",
              icon: "mdi-package-variant-closed",
              name: "posCatalog",
              rights: ["cassa_admin"],
            },
            {
              text: "Geräte",
              icon: "mdi-tablet-dashboard",
              name: "posDevices",
              rights: ["cassa_admin"],
            },
          ],
        },
        {
          icon: "mdi-head-question-outline",
          text: "Live Quiz",
          name: "liveQuizManage",
          rights: ["g_quizManage"],
        },
        /*{
          icon: "mdi-view-dashboard",
          text: "COO",
          rights: ["stat_coo"],
          subItems: [
            {
              text: "Dashboard",
              icon: "mdi-view-dashboard-outline",
              rights: ["stat_coo"],
              name: "cooDashboard",
            },
          ],
        },*/
        {
          icon: "mdi-timer",
          text: "Zeiterfassung",

          rights: ["tt_employeeManage", "tt_management", "tt_office"],
          subItems: [
            {
              text: "Zeiterfassung",
              icon: "mdi-clock-outline",
              rights: ["tt_employeeManage"],
              name: "timeTracker",
            },
            {
              text: "Übersicht",
              icon: "mdi-account-clock-outline",
              rights: ["tt_management"],
              name: "timeTrackerManagement",
            },
            {
              text: "Abrechnung",
              icon: "mdi-file-chart-outline",
              rights: ["tt_office"],
              name: "timeTrackerOffice",
            },
          ],
        },
        {
          icon: "mdi-timer",
          text: "Arbeitsplanung",
          rights: ["tt_office", "sm_create", "stats_transactions"],
          name: "shiftManagementMain",
          subItems: [
            {
              text: "Planung erstellen",
              icon: "mdi-calendar-edit",
              rights: ["tt_office", "sm_create"],
              name: "shiftManagementPlan",
            },
            {
              text: "Einstellungen",
              icon: "mdi-cog-outline",
              rights: ["tt_office", "sm_create"],
              name: "shiftManagementSettings",
            },
            {
              text: "Online Termine",
              icon: "mdi-calendar-clock",
              rights: ["stats_transactions"],
              name: "onlineAppointments",
            },
          ],
        },
        {
          icon: "mdi-chart-line",
          text: "Planung",
          rights: ["stat_planningControlling"],
          subItems: [
            {
              text: "Controlling",
              icon: "mdi-chart-timeline-variant",
              name: "planningDashboard",
              rights: ["stat_planningControlling"],
            },
          ],
        },
        {
          icon: "mdi-chart-line",
          text: "Statistiken",
          rights: ["stat_viewstudio", "stat_admin", "stat_gf", "stat_gf_stud", "stat_leads"],
          role: ["616616c4c659c875cad63525", "61661791c659c875cad63527"], // trainer
          subItems: [
            /*{
                            text: 'Kennzahlen',
                            name: 'homeDashboardFigures',
                            rights: ['stat_viewstudio', 'stat_admin'],
                        },*/
            {
              text: "Black Friday 2025",
              icon: "mdi-sale",
              name: "homeDashboardBlackFridayStatistic",
              rights: ["stats_blackFriday"],
            },
            {
              text: "CL Statistiken",
              icon: "mdi-chart-box-outline",
              name: "homeDashboardStats",
              rights: ["stats_cl_admin"],
            },
            {
              text: "Arbeitskalender",
              icon: "mdi-calendar-clock-outline",
              name: "homeDashboardWorkCalendarStatistics",
              rights: ["stat_admin"],
            },

            {
              text: "Monatsübersicht Center",
              icon: "mdi-calendar-month-outline",
              name: "homeDashboardCenterMonthlyStatistics",
              rights: ["stat_viewstudio", "stat_admin"],
            },
            {
              text: "Transaktionen TAC",
              icon: "mdi-swap-horizontal",
              name: "homeDashboardTransactionStatistics",
              rights: ["stats_transactions"],
            },
            {
              text: "Kunden Übersicht",
              icon: "mdi-account-group-outline",
              name: "homeDashboardUserStatistics",
              rights: ["stat_viewstudio", "stat_admin"],
            },
            {
              text: "Checkins",
              icon: "mdi-login",
              name: "homeDashboardCheckinStatistics",
              rights: ["stat_viewstudio", "stat_admin"],
            },
            {
              text: "Group Fitness",
              icon: "mdi-chart-bar",
              name: "homeDashboardGroupFitnessStatistics",
              rights: ["stat_gf", "stat_admin", "stat_gf_stud"],
            },
            {
              text: "Verträge",
              icon: "mdi-file-chart-outline",
              name: "homeDashboardContractStatistics",
              rights: ["stat_admin", "stat_viewstudio"],
            },
            {
              text: "Pakete",
              icon: "mdi-package-variant-closed",
              name: "homeDashboardContractPackagesStatistics",
              rights: ["stat_admin"],
            },
            {
              text: "Aboentwicklung",
              icon: "mdi-trending-up",
              name: "homeDashboardContractDevelopmentStatistics",
              rights: ["stat_admin"],
            },
            {
              text: "Leads",
              icon: "mdi-account-convert",
              name: "homeDashboardLeadStatistics",
              rights: ["stat_admin", "stat_viewstudio", "stat_leads"],
            },
            {
              text: "Trainer",
              icon: "mdi-whistle-outline",
              name: "homeDashboardTrainerStatistics",
              rights: ["stat_admin", "stat_viewstudio"],
            },
            {
              text: "Trainer Monatsziele",
              icon: "mdi-target",
              name: "homeDashboardTrainerGoals",
              rights: ["stat_admin", "stat_viewstudio"],
              role: ["616616c4c659c875cad63525", "61661791c659c875cad63527"], // trainer
            },
            {
              text: "Monatsziele Statistiken",
              icon: "mdi-target",
              name: "homeDashboardGoalsStatistics",
              rights: ["stat_admin"],
            },
            {
              text: "Mitarbeiterzahlen",
              icon: "mdi-badge-account-outline",
              name: "homeDashboardEmployeeStatistics",
              rights: ["stat_employee", "stat_viewstudio"],
            },
            {
              text: "11-Tage Kampagne",
              icon: "mdi-calendar-range",
              name: "homeDashboardMembercardStatistics",
              rights: ["stat_admin"],
            },
            {
              text: "Anamnese",
              icon: "mdi-clipboard-text-outline",
              name: "homeDashboardAnamneseStatistics",
              rights: ["stat_admin", "stat_viewstudio"],
            },
            {
              text: "Kündigungen",
              icon: "mdi-file-remove-outline",
              name: "homeDashboardTerminationDevelopmentStatistics",
              rights: ["stats_transactions"],
            },
          ],
        },
        {
          icon: "mdi-newspaper-variant-outline",
          text: "Verträge",
          name: "contract",
          rights: ["u_downloadUserList", "c_create_customer", "c_open_contracts", "c_create"],
          subItems: [
            {
              text: "Ernährungsberatung",
              icon: "mdi-food-apple-outline",
              name: "nutritionSales",
              rights: ["c_create_customer", "c_create"],
            },
            { text: "Übersicht", icon: "mdi-format-list-bulleted", name: "contractList", rights: ["c_create_customer", "c_create"] },
            { text: "Offen", icon: "mdi-folder-open-outline", name: "contractManager", rights: ["c_open_contracts", "c_create_customer"] },
            {
              text: "Temporär",
              icon: "mdi-timer-sand",
              name: "contractTemporary",
              rights: ["c_create_customer", "c_open_contracts"],
            },
            {
              text: "Abgelehnt",
              icon: "mdi-close-circle-outline",
              name: "contractRejected",
              rights: ["c_create_customer", "c_open_contracts"],
            },
            { text: "MTT Verträge", icon: "mdi-file-document-outline", name: "mttContractsView", rights: ["d_fetchAllDocs"] },
            { text: "QR Rechnungen", icon: "mdi-qrcode", name: "qrContractsView", rights: ["c_create"] },
            { text: "Einstellungen", icon: "mdi-cog-outline", name: "contractSettings", rights: ["c_create"] },
            { text: "Statistik (abgelehnt)", icon: "mdi-chart-box-outline", name: "contractStats", rights: ["c_create"] },
          ],
        },
        {
          icon: "mdi-newspaper-variant-outline",
          text: "Upgrades",
          name: "contractUpgrades",
          rights: ["c_create_customer", "c_open_contracts", "c_create"],
          subItems: [
            {
              text: "Akzeptiert",
              icon: "mdi-check-circle-outline",
              name: "contractUpgradesAccepted",
              rights: ["c_create_customer", "c_open_contracts"],
            },
            {
              text: "Offen",
              icon: "mdi-clock-outline",
              name: "contractUpgradesOpen",
              rights: ["c_create_customer", "c_open_contracts"],
            },
            {
              text: "Abgelehnt",
              icon: "mdi-close-circle-outline",
              name: "contractUpgradesClosed",
              rights: ["c_create_customer", "c_open_contracts"],
            },
          ],
        },
        {
          icon: "mdi-file-document-multiple-outline",
          text: "Dokumente",

          rights: ["d_v_healthcare", "d_v_timestops", "d_c_timestops", "d_c_timestop", "d_fetchDocs", "cc_view", "d_fetchJobApplications"],
          subItems: [
            {
              text: "Bewerbungen",
              icon: "mdi-account-tie-outline",
              rights: ["d_manageJobApplications", "d_fetchJobApplications"],
              name: "jobApplications",
            },
            {
              text: "Dokumentenablage",
              icon: "mdi-folder-outline",
              rights: ["d_fetchAllDocs", "d_fetchDocs"],
              name: "documentsMain",
            },
            {
              text: "KK-Bestätigungen",
              icon: "mdi-medical-bag",
              rights: ["d_v_healthcare"],
              name: "healthcareView",
            },
            {
              text: "Kreditkartenbelege",
              icon: "mdi-credit-card-outline",
              rights: ["cc_view", "cc_viewAll"],
              name: "creditCardBills",
            },
            {
              text: "Timestops",
              icon: "mdi-timer-off-outline",
              rights: ["d_c_timestops", "d_v_timestops"],
              name: "timestopView",
            },
            {
              text: "Unfallmeldungen",
              icon: "mdi-alert-outline",
              rights: ["d_fetchAccidentProtocols", "d_manageAccidentProtocols"],
              name: "accidentReports",
            },
            {
              text: "Personalanliegen",
              icon: "mdi-account-question-outline",
              rights: ["d_fetchPersonalRequests", "d_managePersonalRequests"],
              name: "personalRequests",
            },
            /*{
                            text: 'Timestop Rechner',
                            rights: ['d_c_timestops', 'd_v_timestops'],
                            name: 'timestopCalculator',
                        },*/
          ],
        },
        /* {
          icon: "mdi-cash-100",
          text: "Zahlungen",

          rights: ["payment_create"],
          subItems: [
            {
              text: "Zahlung erfassen",
              rights: ["payment_create"],
              name: "payment",
            },
            {
              text: "Übersicht",
              rights: ["payment_overview"],
              name: "paymentOverview",
            },
          ],
        },*/
        {
          icon: "mdi-cart",
          text: "Shop",
          rights: ["shop_manage", "shop_purchase_view"],
          subItems: [
            {
              text: "Produkte",
              icon: "mdi-package-variant",
              rights: ["shop_manage"],
              name: "shopProducts",
            },
            {
              text: "Einkäufe",
              icon: "mdi-cart-check",
              rights: ["shop_manage", "shop_purchase_view"],
              name: "shopPurchases",
            },
          ],
        },
        {
          icon: "mdi-nutrition",
          text: "Ernährung",
          rights: ["sebdomspecial"],
          subItems: [
            {
              text: "Rezepte Durchsuchen",
              icon: "mdi-silverware-fork-knife",
              rights: ["nutrition_admin"],
              name: "nutritionSearch",
            },
            {
              text: "Kunden",
              icon: "mdi-account-group-outline",
              rights: ["nutrition_admin"],
              name: "nutritionCustomers",
            },
          ],
        },
        {
          icon: "mdi-bullhorn",
          text: "Marketing",
          name: "communication",
          rights: ["mk_news", "mk_popup", "mk_qrcode", "mk_push", "mk_slider", "g_websitecontent", "d_fetchMarketingStock", "mk_campaigns"],

          subItems: [
            {
              text: "Party",
              name: "communicationParty",
              icon: "mdi-party-popper",
              rights: ["mk_news"],
            },

            {
              text: "Hyrox Score",
              name: "hyroxScore",
              icon: "mdi-list-box",
              rights: ["mk_hyroxPFT"],
            },

            {
              text: "Hyrox Simulation",
              name: "hyroxSimulation",
              icon: "mdi-list-box",
              rights: ["mk_hyroxPFT"],
            },

            {
              text: "PopUps",
              name: "communicationPopupsNew",
              icon: "mdi-cellphone-information",
              rights: ["mk_popup"],
            },
            {
              text: "QR-Code",
              name: "communicationQRCode",
              icon: "mdi-qrcode",
              rights: ["mk_qrcode"],
            },
            {
              text: "Wettbewerbe",
              name: "communicationCompetition",
              icon: "mdi-trophy-outline",
              rights: ["mk_campaigns"],
            },
            /* {
              text: "Kampagnen",
              name: "communicationDashboardCampaign",
              icon: "mdi-bullhorn",
              rights: ["mk_campaigns"],
            },*/
            {
              text: "Push",
              name: "communicationDashboardPush",
              icon: "mdi-cellphone-text",
              rights: ["mk_push"],
            },
            {
              text: "Screen Rechnungen",
              name: "screenBill",
              icon: "mdi-monitor",
              rights: ["mk_screenBill"],
            },
            {
              text: "Lager",
              name: "marketingStock",
              icon: "mdi-warehouse",
              rights: ["d_fetchMarketingStock"],
            },
            {
              text: "Shop",
              name: "marketingShop",
              icon: "mdi-basket",
              rights: ["d_fetchMarketingShop", "d_manageMarketingShop"],
            },
            {
              text: "Slider",
              name: "communicationDashboardSlideManager",
              icon: "mdi-view-gallery",
              rights: ["mk_slider"],
            },
            {
              text: "Webseite",
              name: "websiteContent",
              icon: "mdi-web",
              rights: ["g_websitecontent"],
            },
            {
              text: "Mail Signatur",
              name: "communicationDashboardSignatureGenerator",
              icon: "mdi-email",
              rights: ["mk_emailsignature"],
            },
            {
              text: "Ausdruck generator",
              name: "communicationDashboardPrintGenerator",
              icon: "mdi-file-pdf-box",
              rights: ["mk_emailsignature"],
            },
          ],
        },

        {
          icon: "mdi-dumbbell",
          text: "Geräte",
          rights: ["ex_create", "ex_edit", "ex_delete", "ex_transfer"],
          name: "devicesDashboardList",
        },

        {
          icon: "mdi-account-search",
          text: "Benutzersuche",
          rights: ["u_search"],
          name: "userDashboardSearch",
        },

        {
          icon: "mdi-account-heart",
          text: "Weiterempfehlungen",
          name: "leads",
          rights: ["af_downloadList", "af_view", "af_edit", "af_settingsEdit", "af_leadSettings", "af_cost"],
          subItems: [
            /*    {
              text: "Anfragen Liste",
              name: "leadsDashboardLeadsList",
              rights: ["af_settingsEdit"],
            },*/
            {
              text: "Alle Leads ",
              icon: "mdi-account-multiple",
              name: "leadsDashboardAllLeads",
              rights: ["af_downloadList", "af_view", "af_edit"],
            },
            /*  {
              text: "Referral",
              name: "leadsDashboardReferralList",
              rights: ["af_settingsEdit"],
            },*/
            /*  {
              text: "Referral (Alt)",
              name: "leadsDashboardLeadsDeprecated",
              rights: ["af_settingsEdit"],
            },*/
            {
              icon: "mdi-cog",
              text: "Referral",
              name: "leadsDashboardReferralSettings",
              rights: ["af_settingsEdit"],
            },
            {
              icon: "mdi-cog",
              text: "Leads",
              name: "leadsDashboardSettings",
              rights: ["af_leadSettings"],
            },
            /* {
              text: "Lead Kosten",
              name: "leadsCostManager",
              rights: ["af_cost"],
            },*/
          ],
        },
        /* {
          icon: "mdi-alpha-a-circle-outline",
          text: "Membercard",
          rights: ["m_downloadList", "m_signupUser", "m_viewSubs", "m_createSubs", "m_createMembercard"],
          name: "membercardDashboardAdmin",
        },*/
        {
          icon: "mdi-domain",
          text: "TAC",
          name: "tac",
          rights: ["tac_accessDevices"],
          subItems: [
            {
              text: "Zugangsgeräte",
              icon: "mdi-door-open",
              name: "tacAccessDevices",
              rights: ["tac_accessDevices"],
            },
          ],
        },
        {
          icon: "mdi-clipboard-list-outline",
          text: "Trainingsplan",
          name: "workouts",
          rights: ["tp_create", "tp_viewAll", "tp_feedback"],
          subItems: [
            {
              text: "Bewertungen",
              icon: "mdi-message-star-outline",
              name: "workoutsDashboardFeedback",
              rights: ["tp_feedback"],
            },
          ],
        },
        {
          icon: "mdi-weight-lifter",
          text: "Group Fitness",
          name: "gf",
          rights: [
            "gf_c_create",

            "gf_c_edit",

            "gf_c_delete",

            "gf_c_plan",

            "gf_c_signupRange",

            "r_edit",

            "l_edit",

            "gf_r_viewAll",

            "gf_r_threestars",
            "a_add",
            "a_delete",
            "a_edit",
            "gf_cp_instructor",
            "gf_manageEmployeeCourseContent",
          ],
          subItems: [
            {
              text: "MA-Kurse",
              name: "employeeCoursesAdmin",
              icon: "mdi-calendar-account",
              rights: ["gf_manageEmployeeCourses"],
            },
            {
              text: "HYROX Programme",
              name: "employeeCourseContentAdmin",
              icon: "mdi-clipboard-text-play-outline",
              rights: ["gf_manageEmployeeCourseContent"],
            },
            {
              text: "Kurse",
              name: "groupFitnessDashboardCourses",
              icon: "mdi-list-box",
              rights: ["gf_c_create", "gf_c_edit", "gf_c_delete"],
            },
            {
              text: "Instr. Liste",
              name: "groupFitnessDashboardInstructorList",
              icon: "mdi-list-box",
              rights: ["gf_c_create", "gf_c_edit", "gf_c_delete"],
            },
            {
              text: "Räume",
              name: "groupFitnessDashboardRooms",
              icon: "mdi-map-marker",
              rights: ["r_edit"],
            },
            {
              text: "Labels",
              name: "groupFitnessDashboardLabels",
              icon: "mdi-label",
              rights: ["l_edit"],
            },
            /* {
              text: "Videos",
              name: "groupFitnessDashboardVideos",
              icon: "mdi-folder-play",
              rights: ["a_add", "a_delete", "a_edit"],
            },*/
            {
              text: "Events",
              name: "groupFitnessDashboardEventHome",
              icon: "mdi-calendar",
              rights: ["gf_c_create", "gf_c_edit", "gf_c_delete", "gf_c_plan"],
            },
            {
              text: "Bewertungen",
              name: "groupFitnessDashboardRatings",
              icon: "mdi-comment-quote",
              rights: ["gf_r_viewAll", "gf_r_threestars"],
            },
            {
              text: "Downloads",
              name: "groupFitnessDashboardDownloads",
              icon: "mdi-download-circle-outline",
              rights: ["gf_c_plan", "gf_cp_instructor"],
            },

            {
              text: "Einstellungen",
              name: "groupFitnessDashboardSettings",
              icon: "mdi-cog",
              rights: ["gf_c_create", "gf_c_edit", "gf_c_delete"],
            },
          ],
        },
        {
          icon: "mdi-home",
          text: user && user.studio ? user.studio.name : "Studio",
          name: "studio",
          rights: [
            "g_studioadmin",
            "g_employeemanager",
            "g_daycare",
            "u_search",
            "g_websitecontentstudioadmin",
            "g_websitecontent",
            "ab_view",
            "ab_edit",
            "ab_delete",
          ],
          subItems: [
            {
              text: "Admin",
              icon: "mdi-shield-crown",
              name: "studioEditor",
              rights: ["g_studioadmin"],
            },
            {
              text: "Anamnese Einstellungen",
              icon: "mdi-alpha-a-circle-outline",
              name: "anamneseDashboardSettings",
              rights: ["ab_view", "ab_edit", "ab_delete"],
            },
            {
              text: "Reservationen",
              name: "ReservationsAdmin",
              icon: "mdi-calendar-clock-outline",
              rights: ["reservations_fetch"],
            },
            {
              text: "Mitarbeiter",
              name: "studioEmployee",
              icon: "mdi-account-group",
              rights: ["g_employeemanager"],
            },
            {
              text: "Kinderhort",
              name: "studioDaycare",
              icon: "mdi-teddy-bear",
              rights: ["g_daycare"],
            },
            {
              text: "Öffnungszeiten",
              name: "openingHours",
              icon: "mdi-clock",
              rights: ["g_employeemanager"],
            },
            {
              text: "Webseite",
              name: "websiteContent",
              icon: "mdi-web",
              rights: ["g_websitecontent", "g_websitecontentstudioadmin"],
            },

            /* { text: 'Slim-ME', name: 'studioSlimMe', icon: 'mdi-gymnastics', rights: ['u_search'] },*/
          ],
        },
        {
          icon: "mdi-apps",
          text: "Zurück zur App",
          utility: true,
          function: () => this.pushRoute("/"),
        },
      ]

      const canAccess = (item) => {
        if (!item.rights && !item.role) return true

        return (item.rights && this.userService.hasRightArray(item.rights)) || (item.role && item.role.some((role) => this.userService.hasRole(role)))
      }

      const visibleItems = items.reduce((result, item) => {
        if (!canAccess(item)) return result

        const visibleItem = { ...item }
        if (item.subItems) {
          visibleItem.subItems = item.subItems.filter(canAccess)
          if (!visibleItem.subItems.length) {
            if (!visibleItem.name) return result
            delete visibleItem.subItems
          }
        }

        result.push(visibleItem)
        return result
      }, [])

      return visibleItems.sort((a, b) => {
        if (a.text === "Home") return -1
        if (b.text === "Home") return 1
        if (a.utility) return 1
        if (b.utility) return -1
        return a.text.localeCompare(b.text, "de-CH", { sensitivity: "base" })
      })
    },
    filteredItems() {
      const query = (this.searchQuery || "").trim().toLocaleLowerCase("de-CH")
      if (!query) return this.allowedItems

      return this.allowedItems.reduce((result, item) => {
        if (item.utility) {
          result.push(item)
          return result
        }

        const itemMatches = `${item.text || ""} ${item.name || ""}`.toLocaleLowerCase("de-CH").includes(query)
        if (itemMatches) {
          result.push(item)
          return result
        }

        if (item.subItems) {
          const matchingSubItems = item.subItems.filter((subItem) =>
            `${subItem.text || ""} ${subItem.name || ""}`.toLocaleLowerCase("de-CH").includes(query),
          )

          if (matchingSubItems.length) result.push({ ...item, subItems: matchingSubItems })
        }

        return result
      }, [])
    },
    dashboardQuickLinkOptions() {
      const options = []
      const usedIds = new Set()

      const addOption = (item, parentItem = null) => {
        if (!item || !item.name || item.name === "dashboardHome") return

        const parentKey = parentItem ? this.groupKey(parentItem) : "root"
        const parentText = parentItem ? parentItem.text : "Bereiche"
        const text = item.text || item.name
        const id = `route:${encodeURIComponent(item.name)}:${encodeURIComponent(parentKey)}`

        if (usedIds.has(id)) return
        usedIds.add(id)
        options.push({
          id,
          icon: this.navigationIcon(item, parentItem && parentItem.icon),
          text,
          searchText: `${text} ${parentText}`,
          parentText,
          location: { name: item.name },
          sourceItem: item,
          parentItem,
        })
      }

      this.allowedItems.forEach((item) => {
        if (item.utility) return
        if (item.subItems) item.subItems.forEach((subItem) => addOption(subItem, item))
        else addOption(item)
      })

      return options
    },
    navigationItems() {
      return this.filteredItems.filter((item) => !item.utility)
    },
    utilityItems() {
      return this.filteredItems.filter((item) => item.utility)
    },
  },
  watch: {
    dashboardQuickLinkOptions: {
      immediate: true,
      handler(options) {
        this.$emit("dashboard-quick-link-options", options)
      },
    },
    drawerstate(isOpen) {
      this.drawer = this.isMobile ? (this.isDrawerControlled ? Boolean(isOpen) : true) : true
    },
    isMobile(isMobile) {
      const drawerWasOpen = this.drawer
      this.drawer = isMobile ? (this.isDrawerControlled ? Boolean(this.drawerstate) : false) : true
      this.isHovering = false
      this.isTouchExpanded = false
      this.clearTouchRevealClick()
      this.hasNavigationFocus = false
      this.previouslyFocusedElement = null
      if (!isMobile) {
        this.setBackgroundInert(false)
        if (this.isDrawerControlled) this.$emit("update:drawerstate", false)
      } else if (drawerWasOpen && this.drawer) this.activateMobileDialog()
    },
    drawer(isOpen) {
      if (!this.isMobile) return
      this.$emit("update:drawerstate", isOpen)

      if (isOpen) {
        this.activateMobileDialog()
      } else {
        this.searchQuery = ""
        this.setBackgroundInert(false)
        const previousElement = this.previouslyFocusedElement
        this.previouslyFocusedElement = null
        this.$nextTick(() => {
          if (previousElement && document.contains(previousElement)) previousElement.focus()
        })
      }
    },
    "$route.fullPath"() {
      if (this.activeNavigationRouteName !== this.$route.name) {
        this.activeNavigationGroup = null
        this.activeNavigationRouteName = null
      }
      this.searchQuery = ""
      this.isTouchExpanded = false
      this.clearTouchRevealClick()
      if (!this.keyboardNavigationActive) this.hasNavigationFocus = false
      if (this.isMobile) this.drawer = false
    },
    reservedLayoutWidth() {
      this.emitLayoutWidth()
    },
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    openDashboardQuickLink(option) {
      if (!option || !option.location) return
      if (option.sourceItem) this.handleItemSelection(option.sourceItem, option.parentItem)
      this.pushRoute(option.location)
    },
    navigationIcon(item, parentIcon = null) {
      if (!item) return parentIcon || "mdi-circle-outline"
      return item.icon || parentIcon || "mdi-circle-outline"
    },
    emitLayoutWidth() {
      if (!this._isMounted) return
      this.$emit("layout-width", this.reservedLayoutWidth)
    },
    activateMobileDialog() {
      if (!this.isMobile || !this.drawer) return

      const activeElement = document.activeElement
      const focusIsInsideDrawer = this.$el && activeElement && this.$el.contains(activeElement)
      this.previouslyFocusedElement = activeElement && !focusIsInsideDrawer ? activeElement : null
      this.$nextTick(() => {
        if (!this.isMobile || !this.drawer) return
        this.setBackgroundInert(true)
        const closeButton = this.$refs.closeButton
        if (closeButton && closeButton.$el) closeButton.$el.focus()
      })
    },
    updatePointerCapability(event) {
      this.canHover = event ? event.matches : Boolean(this.pointerQuery && this.pointerQuery.matches)
      if (!this.canHover) this.isHovering = false
    },
    handlePointerEnter() {
      if (!this.canHover || this.isMobile || this.isPinned) return
      window.clearTimeout(this.hoverCloseTimer)
      this.isHovering = true
    },
    handlePointerLeave() {
      if (!this.canHover || this.isMobile || this.isPinned) return
      window.clearTimeout(this.hoverCloseTimer)
      this.hoverCloseTimer = window.setTimeout(() => {
        this.isHovering = false
      }, 220)
    },
    handleNavigationPointerInteraction() {
      this.keyboardNavigationActive = false
      this.hasNavigationFocus = false
    },
    handleNavigationTouchStart() {
      this.handleNavigationPointerInteraction()
      this.isTouchCapable = true

      if (this.isMobile) return
      if (this.navigationExpanded) {
        this.clearTouchRevealClick()
        return
      }

      this.isTouchExpanded = true
      this.suppressNextNavigationClick = true
    },
    scheduleTouchRevealReset() {
      this.$nextTick(() => {
        const activeElement = document.activeElement
        const isEditable =
          activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.isContentEditable)

        if (activeElement && !isEditable && this.$el && this.$el.contains(activeElement)) activeElement.blur()
      })

      if (!this.suppressNextNavigationClick) return
      window.clearTimeout(this.touchRevealTimer)
      this.touchRevealTimer = window.setTimeout(() => this.clearTouchRevealClick(), 650)
    },
    clearTouchRevealClick() {
      window.clearTimeout(this.touchRevealTimer)
      this.touchRevealTimer = null
      this.suppressNextNavigationClick = false
    },
    handleNavigationClickCapture(event) {
      if (!this.suppressNextNavigationClick) return

      this.clearTouchRevealClick()
      event.preventDefault()
      event.stopPropagation()
    },
    handleDocumentTouchStart(event) {
      if (this.isMobile || !this.isTouchExpanded || (this.$el && this.$el.contains(event.target))) return
      this.isTouchExpanded = false
      this.clearTouchRevealClick()
    },
    handleFocusIn() {
      if (this.isMobile || !this.keyboardNavigationActive) return
      window.clearTimeout(this.focusCloseTimer)
      this.hasNavigationFocus = true
    },
    handleFocusOut(event) {
      if (this.isMobile) return
      window.clearTimeout(this.focusCloseTimer)
      this.focusCloseTimer = window.setTimeout(() => {
        if (!this.$el.contains(event.relatedTarget || document.activeElement)) this.hasNavigationFocus = false
      }, 0)
    },
    handleBrandAction() {
      if (!this.isMobile) {
        this.togglePinned()
        return
      }

      if (!this.isItemActive({ name: "dashboardHome" })) this.pushRoute({ name: "dashboardHome" })
      this.closeAfterNavigation()
    },
    setPinned(isPinned) {
      this.isPinned = Boolean(isPinned)
      this.isHovering = false

      try {
        localStorage.setItem(this.pinStorageKey, String(this.isPinned))
      } catch (error) {
        // Die Sidebar funktioniert auch, wenn lokale Präferenzen blockiert sind.
      }
    },
    togglePinned() {
      this.isTouchExpanded = false
      this.setPinned(!this.isPinned)
    },
    expandGroupForTouch() {
      if (this.isMobile || this.isPinned) return
      this.isTouchExpanded = true
    },
    groupKey(item) {
      return item ? item.name || item.text : null
    },
    isGroupExpanded(item) {
      return Boolean(this.searchQuery) || this.isItemActive(item) || this.expandedGroups.includes(this.groupKey(item))
    },
    handleGroupToggle(item, isExpanded) {
      const groupKey = this.groupKey(item)
      const groupIndex = this.expandedGroups.indexOf(groupKey)

      if (isExpanded && groupIndex === -1) this.expandedGroups.push(groupKey)
      if (!isExpanded && groupIndex !== -1) this.expandedGroups.splice(groupIndex, 1)
    },
    closeDrawer() {
      this.drawer = false
    },
    closeAfterNavigation() {
      this.searchQuery = ""
      this.isTouchExpanded = false
      this.clearTouchRevealClick()
      if (!this.keyboardNavigationActive) this.hasNavigationFocus = false
      if (this.isMobile) this.$nextTick(() => (this.drawer = false))
    },
    handleItemSelection(item, parentItem = null) {
      const navigationItems = this.allowedItems.filter((entry) => !entry.utility)
      const matchingEntries = item && item.name ? this.findItemEntriesByRouteName(navigationItems, item.name) : []

      if (parentItem && matchingEntries.length > 1) {
        const selectedGroup = this.groupKey(parentItem)
        const duplicateGroups = matchingEntries.filter((entry) => entry.parent).map((entry) => this.groupKey(entry.parent))

        this.activeNavigationGroup = selectedGroup
        this.activeNavigationRouteName = item.name
        this.expandedGroups = this.expandedGroups.filter((group) => !duplicateGroups.includes(group) || group === selectedGroup)
        if (!this.expandedGroups.includes(selectedGroup)) this.expandedGroups.push(selectedGroup)
      } else {
        this.activeNavigationGroup = null
        this.activeNavigationRouteName = null
      }

      if (typeof item.function === "function") {
        item.function()
        return
      }

      this.closeAfterNavigation()
    },
    pushRoute(location) {
      const navigation = this.$router.push(location)
      if (navigation && typeof navigation.catch === "function") {
        navigation.catch((error) => {
          if (!error || error.name !== "NavigationDuplicated") throw error
        })
      }
    },
    isPathActive(path) {
      const currentPath = this.$route.path || ""
      return currentPath === path || currentPath.startsWith(`${path}/`)
    },
    isItemActive(item, parentItem = null) {
      if (!item) return false
      const routeMatches = item.name && (item.exact ? this.$route.name === item.name : this.isRouteNameActive(item.name, Boolean(item.subItems)))

      if (routeMatches) {
        const matchingEntries = this.findItemEntriesByRouteName(this.navigationItems, item.name)
        if (matchingEntries.length > 1) return this.groupKey(parentItem) === this.resolveActiveGroupForRoute(item.name, matchingEntries)

        if (item.subItems && this.$route.name) {
          const currentRouteEntries = this.findItemEntriesByRouteName(this.navigationItems, this.$route.name)
          const containsCurrentRoute = item.subItems.some((subItem) => subItem.name === this.$route.name)

          if (containsCurrentRoute && currentRouteEntries.length > 1) {
            const activeGroup = this.resolveActiveGroupForRoute(this.$route.name, currentRouteEntries)
            if (activeGroup && activeGroup !== this.groupKey(item)) return false
          }
        }

        return true
      }
      return Boolean(item.subItems && item.subItems.some((subItem) => this.isItemActive(subItem, item)))
    },
    findItemEntriesByRouteName(items, routeName, parent = null, result = []) {
      items.forEach((item) => {
        if (item.name === routeName) result.push({ item, parent })
        if (item.subItems) this.findItemEntriesByRouteName(item.subItems, routeName, item, result)
      })
      return result
    },
    resolveActiveGroupForRoute(routeName, matchingEntries = null) {
      const entries = matchingEntries || this.findItemEntriesByRouteName(this.navigationItems, routeName)
      const availableGroupKeys = entries.filter((entry) => entry.parent).map((entry) => this.groupKey(entry.parent))

      if (this.activeNavigationRouteName === routeName && this.activeNavigationGroup && availableGroupKeys.includes(this.activeNavigationGroup)) {
        return this.activeNavigationGroup
      }

      const matchedRouteNames = new Set((this.$route.matched || []).map((route) => route.name).filter(Boolean))
      const owningEntry = entries.find((entry) => entry.parent && entry.parent.name && matchedRouteNames.has(entry.parent.name))
      if (owningEntry) return this.groupKey(owningEntry.parent)

      const firstGroupedEntry = entries.find((entry) => entry.parent)
      return firstGroupedEntry ? this.groupKey(firstGroupedEntry.parent) : null
    },
    isRouteNameActive(routeName, includeMatched = false) {
      if (!routeName) return false
      if (this.$route.name === routeName) return true
      return Boolean(includeMatched && this.$route.matched && this.$route.matched.some((route) => route.name === routeName))
    },
    handleGlobalKeydown(event) {
      if (event.defaultPrevented) return
      if (event.key === "Tab") this.keyboardNavigationActive = true

      if (event.key === "Escape" && this.isMobile && this.drawer) {
        event.preventDefault()
        this.closeDrawer()
        return
      }

      const target = event.target
      const isEditable = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      const activeModal = document.querySelector('[aria-modal="true"]')

      if (
        event.key !== "/" ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isEditable ||
        (activeModal && activeModal !== this.$el && !this.$el.contains(activeModal)) ||
        (this.isMobile && !this.drawer)
      ) {
        return
      }

      event.preventDefault()
      if (!this.isMobile) {
        this.keyboardNavigationActive = true
        this.hasNavigationFocus = true
      }
      this.$nextTick(() => {
        const search = this.$refs.navSearch
        if (search && typeof search.focus === "function") search.focus()
      })
    },
    trapFocus(event) {
      if (!this.isMobile || !this.drawer) return

      const focusableElements = Array.from(
        this.$el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      ).filter((element) => element.offsetParent !== null)

      if (!focusableElements.length) return
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    },
    setBackgroundInert(shouldInert) {
      if (!shouldInert) {
        this.inertedElements.forEach(({ element, hadInert, inertValue, ariaHidden }) => {
          element.inert = inertValue
          if (!hadInert) element.removeAttribute("inert")
          if (ariaHidden === null) element.removeAttribute("aria-hidden")
          else element.setAttribute("aria-hidden", ariaHidden)
        })
        this.inertedElements = []
        return
      }

      if (!this.isMobile || !this.drawer || this.inertedElements.length || !this.$el) return

      const appRoot = this.$el.closest("[data-app]") || document.body
      let currentElement = this.$el
      const elementsToInert = []

      while (currentElement && currentElement !== appRoot) {
        const parent = currentElement.parentElement
        if (!parent) break

        Array.from(parent.children).forEach((sibling) => {
          if (sibling === currentElement || sibling.classList.contains("v-overlay") || sibling.classList.contains("v-navigation-drawer__scrim")) {
            return
          }

          if (!elementsToInert.includes(sibling)) elementsToInert.push(sibling)
        })

        currentElement = parent
      }

      this.inertedElements = elementsToInert.map((element) => {
        const state = {
          element,
          hadInert: element.hasAttribute("inert"),
          inertValue: Boolean(element.inert),
          ariaHidden: element.getAttribute("aria-hidden"),
        }

        element.inert = true
        element.setAttribute("inert", "")
        element.setAttribute("aria-hidden", "true")
        return state
      })
    },
  },
  created() {
    const pinPreference = getPinPreferenceContext()
    this.isTouchCapable = pinPreference.isTouchCapable
    this.pinStorageKey = pinPreference.storageKey

    try {
      const storedPinnedPreference = typeof localStorage !== "undefined" ? localStorage.getItem(this.pinStorageKey) : null
      this.isPinned = storedPinnedPreference === null ? pinPreference.defaultPinned : storedPinnedPreference === "true"
    } catch (error) {
      this.isPinned = pinPreference.defaultPinned
    }

    this.drawer = this.isMobile ? (this.isDrawerControlled ? Boolean(this.drawerstate) : false) : true
    this.setNavigationbarVisibility(false)
  },
  mounted() {
    this.$emit("dashboard-quick-link-options", this.dashboardQuickLinkOptions)

    if (typeof window !== "undefined") {
      this.pointerQuery = window.matchMedia("(hover: hover) and (pointer: fine)")
      this.updatePointerCapability()

      if (typeof this.pointerQuery.addEventListener === "function") {
        this.pointerQuery.addEventListener("change", this.updatePointerCapability)
      } else {
        this.pointerQuery.addListener(this.updatePointerCapability)
      }

      window.addEventListener("keydown", this.handleGlobalKeydown)
      document.addEventListener("touchstart", this.handleDocumentTouchStart, true)
    }

    this.$nextTick(this.emitLayoutWidth)
  },
  beforeDestroy() {
    window.clearTimeout(this.hoverCloseTimer)
    window.clearTimeout(this.focusCloseTimer)
    window.clearTimeout(this.touchRevealTimer)
    this.setBackgroundInert(false)

    if (this.pointerQuery) {
      if (typeof this.pointerQuery.removeEventListener === "function") {
        this.pointerQuery.removeEventListener("change", this.updatePointerCapability)
      } else {
        this.pointerQuery.removeListener(this.updatePointerCapability)
      }
    }

    if (typeof window !== "undefined") {
      window.removeEventListener("keydown", this.handleGlobalKeydown)
      document.removeEventListener("touchstart", this.handleDocumentTouchStart, true)
    }
    this.setNavigationbarVisibility(true)
  },
}
</script>

<style scoped>
.sidebar {
  --sidebar-bg: var(--dashboard-bg, #181a1e);
  --sidebar-surface: rgba(255, 255, 255, 0.045);
  --sidebar-surface-hover: rgba(255, 255, 255, 0.075);
  --sidebar-surface-pressed: rgba(255, 255, 255, 0.11);
  --sidebar-border: rgba(255, 255, 255, 0.075);
  --sidebar-divider: rgba(255, 255, 255, 0.065);
  --sidebar-text: rgba(255, 255, 255, 0.94);
  --sidebar-muted: rgba(255, 255, 255, 0.61);
  --sidebar-subtle: rgba(255, 255, 255, 0.4);
  --sidebar-icon: rgba(255, 255, 255, 0.7);
  --sidebar-accent: var(--v-primary-base, #8b5cf6);
  --sidebar-accent-soft: rgba(255, 255, 255, 0.085);
  --sidebar-accent-ring: rgba(255, 255, 255, 0.18);
  --sidebar-ease-out: cubic-bezier(0.22, 1, 0.36, 1);
  --sidebar-ease-standard: cubic-bezier(0.2, 0.8, 0.2, 1);

  z-index: 20 !important;
  overflow: hidden;
  color: var(--sidebar-text) !important;
  border-right: 1px solid var(--sidebar-border);
  border-radius: 0 22px 22px 0;
  background: radial-gradient(circle at 22px 44px, var(--sidebar-accent-soft), transparent 190px),
    linear-gradient(155deg, #202329 0%, var(--sidebar-bg) 52%, #15171a 100%) !important;
  box-shadow: 18px 0 54px rgba(0, 0, 0, 0.27), inset -1px 0 rgba(255, 255, 255, 0.025);
  transition: width 260ms var(--sidebar-ease-out), transform 300ms var(--sidebar-ease-out), visibility 300ms,
    border-radius 260ms var(--sidebar-ease-out), box-shadow 260ms ease !important;
}

.sidebar__ambient {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.sidebar__ambient::before,
.sidebar__ambient::after {
  position: absolute;
  content: "";
  border-radius: 999px;
  filter: blur(1px);
  opacity: 0.7;
}

.sidebar__ambient::before {
  top: -110px;
  left: -105px;
  width: 240px;
  height: 240px;
  border: 1px solid rgba(255, 255, 255, 0.055);
}

.sidebar__ambient::after {
  right: -90px;
  bottom: 12%;
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, var(--sidebar-accent-soft), transparent 68%);
}

.sidebar__shell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding-top: env(safe-area-inset-top, 0px);
}

.sidebar__header {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: 46px minmax(0, 1fr) 38px;
  gap: 11px;
  align-items: center;
  min-width: 268px;
  min-height: 76px;
  padding: 14px 14px 10px;
}

.sidebar__brand-mark {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  padding: 0;
  overflow: visible;
  color: #fff;
  font: inherit;
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: -0.08em;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  outline: 0;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.035)), #252830;
  box-shadow: 0 9px 24px rgba(0, 0, 0, 0.24), inset 0 1px rgba(255, 255, 255, 0.1);
  transition: transform 180ms var(--sidebar-ease-out), border-color 180ms ease, box-shadow 180ms ease;
  -webkit-tap-highlight-color: transparent;
}

.sidebar__brand-glyph {
  color: var(--sidebar-text) !important;
  filter: drop-shadow(0 3px 7px rgba(0, 0, 0, 0.28));
}

.sidebar__brand-action {
  position: absolute;
  right: -5px;
  bottom: -4px;
  display: flex;
  width: 18px;
  height: 18px;
  color: #fff !important;
  border: 2px solid #1d1f24;
  border-radius: 50%;
  background: var(--sidebar-accent);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.32);
}

.sidebar:not(.sidebar--touch) .sidebar__brand-mark:hover {
  border-color: rgba(255, 255, 255, 0.17);
  transform: translateY(-1px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28), inset 0 1px rgba(255, 255, 255, 0.14);
}

.sidebar__brand-mark:active {
  transform: translateY(0) scale(0.97);
}

.sidebar__brand-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  white-space: nowrap;
}

.sidebar__brand-copy strong {
  overflow: hidden;
  color: var(--sidebar-text);
  font-size: 1.125rem;
  font-weight: 850;
  line-height: 1.05;
  letter-spacing: -0.035em;
  text-overflow: ellipsis;
}

.sidebar__brand-copy strong span {
  color: var(--sidebar-accent);
}

.sidebar__brand-copy small {
  margin-top: 5px;
  color: var(--sidebar-subtle);
  font-size: 0.75rem;
  font-weight: 750;
  line-height: 1;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.sidebar__header-action {
  width: 38px !important;
  height: 38px !important;
  color: var(--sidebar-muted) !important;
  background: rgba(255, 255, 255, 0.035);
  transition: color 160ms ease, background 160ms ease, transform 160ms var(--sidebar-ease-out) !important;
}

.sidebar:not(.sidebar--touch) .sidebar__header-action:hover {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.085);
  transform: rotate(-4deg);
}

.sidebar__reveal {
  opacity: 1;
  transform: translateX(0);
  transition: opacity 170ms ease 45ms, transform 200ms var(--sidebar-ease-out) 35ms;
}

.sidebar:not(.sidebar--expanded) .sidebar__reveal {
  pointer-events: none;
  opacity: 0;
  transform: translateX(-7px);
  transition-delay: 0ms;
}

.sidebar__search {
  flex: 0 0 auto;
  width: 100%;
  max-height: 64px;
  min-width: 268px;
  padding: 4px 14px 10px;
  overflow: hidden;
  transition: max-height 240ms var(--sidebar-ease-out), padding 240ms var(--sidebar-ease-out), opacity 170ms ease 45ms,
    transform 200ms var(--sidebar-ease-out) 35ms;
}

.sidebar:not(.sidebar--expanded) .sidebar__search {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

::v-deep .sidebar__search-field.v-text-field.v-text-field--solo > .v-input__control > .v-input__slot {
  min-height: 44px;
  margin: 0;
  padding: 0 13px;
  color: var(--sidebar-text);
  border: 1px solid rgba(255, 255, 255, 0.065);
  background: rgba(255, 255, 255, 0.045) !important;
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.025) !important;
  transition: border-color 170ms ease, background 170ms ease, box-shadow 170ms ease;
}

::v-deep .sidebar__search-field.v-input--is-focused > .v-input__control > .v-input__slot {
  border-color: var(--sidebar-accent);
  background: rgba(255, 255, 255, 0.065) !important;
  box-shadow: 0 0 0 3px var(--sidebar-accent-soft) !important;
}

::v-deep .sidebar__search-field input {
  color: var(--sidebar-text) !important;
  font-size: 0.8125rem;
  font-weight: 560;
}

::v-deep .sidebar__search-field input::placeholder {
  color: var(--sidebar-subtle) !important;
  opacity: 1;
}

::v-deep .sidebar__search-field .v-icon {
  color: var(--sidebar-muted) !important;
}

.sidebar__shortcut {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  color: var(--sidebar-subtle);
  font: inherit;
  font-size: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.17);
  box-shadow: inset 0 -1px rgba(255, 255, 255, 0.04);
}

.sidebar__navigation {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.sidebar__navigation::before,
.sidebar__navigation::after {
  position: absolute;
  right: 0;
  left: 0;
  z-index: 2;
  height: 14px;
  pointer-events: none;
  content: "";
}

.sidebar__navigation::before {
  top: 0;
  background: linear-gradient(to bottom, rgba(24, 26, 30, 0.82), transparent);
}

.sidebar__navigation::after {
  bottom: 0;
  background: linear-gradient(to top, rgba(21, 23, 26, 0.82), transparent);
}

.sidebar__scroll {
  height: 100%;
  padding: 6px 0 12px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.sidebar__scroll::-webkit-scrollbar {
  width: 5px;
}

.sidebar__scroll::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar__scroll::-webkit-scrollbar-thumb {
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.13);
}

.sidebar__section {
  min-width: 268px;
}

.sidebar__section-label {
  height: 1.375rem;
  margin: 0;
  padding: 0.1875rem 1.25rem 0;
  overflow: hidden;
  color: var(--sidebar-subtle);
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: height 220ms var(--sidebar-ease-out), opacity 170ms ease, transform 200ms var(--sidebar-ease-out);
}

.sidebar:not(.sidebar--expanded) .sidebar__section-label {
  height: 0.625rem;
  padding-top: 0;
}

::v-deep .sidebar__list.v-list {
  padding: 0 10px;
  color: var(--sidebar-text);
  background: transparent !important;
}

.sidebar__entry {
  min-width: 248px;
}

::v-deep .sidebar__item.v-list-item,
::v-deep .sidebar__group > .v-list-group__header {
  position: relative;
  min-height: 48px;
  margin: 3px 0;
  padding: 0 10px !important;
  overflow: hidden;
  color: var(--sidebar-muted) !important;
  text-decoration: none !important;
  border: 1px solid transparent;
  border-radius: 14px;
  background: transparent;
  transition: color 150ms ease, background 150ms ease, border-color 150ms ease, transform 150ms var(--sidebar-ease-standard), box-shadow 180ms ease;
}

::v-deep .sidebar__item.v-list-item::before,
::v-deep .sidebar__group > .v-list-group__header::before,
::v-deep .sidebar__subitem.v-list-item::before {
  opacity: 0 !important;
}

::v-deep .sidebar__item-icon.v-list-item__icon {
  display: inline-flex;
  flex: 0 0 36px;
  align-self: center !important;
  align-items: center;
  justify-content: center;
  width: 36px;
  min-width: 36px;
  height: 36px;
  margin: 0 12px 0 0 !important;
  border: 1px solid transparent;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.025);
  transition: color 160ms ease, background 160ms ease, border-color 160ms ease, transform 180ms var(--sidebar-ease-out);
}

::v-deep .sidebar__item-icon .v-icon {
  display: inline-flex;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--sidebar-icon) !important;
  transition: color 160ms ease, transform 180ms var(--sidebar-ease-out);
}

::v-deep .sidebar__item-copy.v-list-item__content,
::v-deep .sidebar__subitem-copy.v-list-item__content {
  align-self: stretch;
  justify-content: center;
  min-width: 0;
  padding: 0 !important;
}

::v-deep .sidebar__item-copy.v-list-item__content {
  opacity: 1;
  transform: translateX(0);
  transition: opacity 150ms ease 35ms, transform 190ms var(--sidebar-ease-out) 25ms;
}

.sidebar:not(.sidebar--expanded) ::v-deep .sidebar__item-copy.v-list-item__content {
  opacity: 0;
  transform: translateX(-7px);
  transition-delay: 0ms;
}

::v-deep .sidebar__item .v-list-item__title,
::v-deep .sidebar__group > .v-list-group__header .v-list-item__title {
  color: inherit;
  font-size: 0.84375rem;
  font-weight: 630;
  line-height: 20px;
  letter-spacing: -0.01em;
}

::v-deep .sidebar__group > .v-list-group__header .v-list-group__header__append-icon {
  min-width: 24px;
  margin: 0;
}

::v-deep .sidebar__group > .v-list-group__header .v-list-group__header__append-icon .v-icon {
  color: var(--sidebar-subtle) !important;
  font-size: 1.25rem !important;
  transition: color 160ms ease, transform 220ms var(--sidebar-ease-out);
}

@media (hover: hover) and (pointer: fine) {
  .sidebar:not(.sidebar--touch) ::v-deep .sidebar__item.v-list-item:hover,
  .sidebar:not(.sidebar--touch) ::v-deep .sidebar__group > .v-list-group__header:hover,
  .sidebar:not(.sidebar--touch) ::v-deep .sidebar__subitem.v-list-item:hover {
    color: var(--sidebar-text) !important;
    border-color: rgba(255, 255, 255, 0.045);
    background: var(--sidebar-surface-hover);
    transform: translateX(2px);
  }

  .sidebar:not(.sidebar--touch) ::v-deep .sidebar__item:hover .sidebar__item-icon,
  .sidebar:not(.sidebar--touch) ::v-deep .sidebar__group > .v-list-group__header:hover .sidebar__item-icon {
    background: rgba(255, 255, 255, 0.06);
    transform: scale(1.035);
  }

  .sidebar:not(.sidebar--touch) ::v-deep .sidebar__item:hover .sidebar__item-icon .v-icon,
  .sidebar:not(.sidebar--touch) ::v-deep .sidebar__group > .v-list-group__header:hover .sidebar__item-icon .v-icon {
    color: #fff !important;
  }
}

.sidebar--touch ::v-deep .sidebar__item.v-list-item:not(.sidebar__item--active),
.sidebar--touch ::v-deep .sidebar__group:not(.sidebar__group--active) > .v-list-group__header,
.sidebar--touch ::v-deep .sidebar__subitem.v-list-item:not(.sidebar__subitem--active) {
  color: var(--sidebar-muted) !important;
  border-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  transform: none !important;
}

.sidebar--touch ::v-deep .sidebar__item:not(.sidebar__item--active) .sidebar__item-icon,
.sidebar--touch ::v-deep .sidebar__group:not(.sidebar__group--active) > .v-list-group__header .sidebar__item-icon,
.sidebar--touch ::v-deep .sidebar__subitem:not(.sidebar__subitem--active) .sidebar__subitem-icon {
  transform: none !important;
}

.sidebar--touch ::v-deep .sidebar__item:not(.sidebar__item--active) .sidebar__item-icon .v-icon,
.sidebar--touch ::v-deep .sidebar__group:not(.sidebar__group--active) > .v-list-group__header .sidebar__item-icon .v-icon {
  color: var(--sidebar-icon) !important;
  transform: none !important;
}

.sidebar--touch ::v-deep .sidebar__subitem:not(.sidebar__subitem--active) .sidebar__subitem-icon .v-icon {
  color: var(--sidebar-muted) !important;
  transform: none !important;
}

.sidebar--touch ::v-deep .v-ripple__container {
  display: none !important;
}

.sidebar--touch .sidebar__brand-mark,
.sidebar--touch ::v-deep .v-btn,
.sidebar--touch ::v-deep .v-list-item {
  -webkit-tap-highlight-color: transparent;
}

::v-deep .sidebar__item.v-list-item:active,
::v-deep .sidebar__group > .v-list-group__header:active,
::v-deep .sidebar__subitem.v-list-item:active {
  background: var(--sidebar-surface-pressed);
  transform: scale(0.985);
}

::v-deep .sidebar__item--active.v-list-item,
::v-deep .sidebar__group--active > .v-list-group__header {
  color: var(--sidebar-text) !important;
  border-color: rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.085);
  background: color-mix(in srgb, var(--sidebar-accent) 14%, transparent);
  box-shadow: inset 3px 0 var(--sidebar-accent), 0 8px 22px rgba(0, 0, 0, 0.1);
}

::v-deep .sidebar__item--active .sidebar__item-icon,
::v-deep .sidebar__group--active > .v-list-group__header .sidebar__item-icon {
  border-color: rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.085);
}

::v-deep .sidebar__item--active .sidebar__item-icon .v-icon,
::v-deep .sidebar__group--active > .v-list-group__header .sidebar__item-icon .v-icon {
  color: var(--sidebar-accent) !important;
  transform: scale(1.06);
}

.sidebar__quick-dot {
  position: absolute;
  right: 14px;
  width: 6px;
  height: 6px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 50%;
  background: var(--sidebar-accent);
  box-shadow: 0 0 0 3px var(--sidebar-accent-soft);
  opacity: 0.72;
}

::v-deep .sidebar__group .v-list-group__items {
  position: relative;
  max-height: var(--sidebar-group-height, 960px);
  padding: 3px 0 7px 48px;
  overflow: hidden;
  opacity: 1;
  transition: max-height 260ms var(--sidebar-ease-out) 55ms, padding 230ms var(--sidebar-ease-out) 45ms, opacity 150ms ease 70ms;
}

.sidebar:not(.sidebar--expanded) ::v-deep .sidebar__group .v-list-group__items {
  max-height: 0 !important;
  padding-top: 0;
  padding-bottom: 0;
  pointer-events: none;
  opacity: 0;
  transition-delay: 0ms;
}

::v-deep .sidebar__group .v-list-group__items::before {
  position: absolute;
  top: 7px;
  bottom: 11px;
  left: 29px;
  width: 1px;
  content: "";
  background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.1) 12%, rgba(255, 255, 255, 0.1) 88%, transparent);
}

::v-deep .sidebar__subitem.v-list-item {
  position: relative;
  min-height: 42px;
  margin: 2px 10px 2px 0;
  padding: 0 9px !important;
  color: var(--sidebar-muted) !important;
  text-decoration: none !important;
  border: 1px solid transparent;
  border-radius: 11px;
  transition: color 150ms ease, background 150ms ease, border-color 150ms ease, transform 150ms var(--sidebar-ease-standard);
}

::v-deep .sidebar__subitem-icon.v-list-item__icon {
  display: inline-flex;
  flex: 0 0 24px;
  align-self: center !important;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
  margin: 0 8px 0 0 !important;
  border: 1px solid rgba(255, 255, 255, 0.035);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
}

::v-deep .sidebar__subitem-icon .v-icon {
  display: inline-flex;
  flex: 0 0 18px;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: var(--sidebar-muted) !important;
}

::v-deep .sidebar__subitem .v-list-item__title {
  overflow: hidden;
  font-size: 0.78125rem;
  font-weight: 560;
  line-height: 18px;
  text-overflow: ellipsis;
}

::v-deep .sidebar__subitem--active.v-list-item {
  color: var(--sidebar-text) !important;
  border-color: rgba(255, 255, 255, 0.055);
  background: rgba(255, 255, 255, 0.065);
}

::v-deep .sidebar__subitem--active .sidebar__subitem-icon {
  border-color: rgba(255, 255, 255, 0.075);
  background: var(--sidebar-accent-soft);
}

::v-deep .sidebar__subitem--active .sidebar__subitem-icon .v-icon {
  color: var(--sidebar-accent) !important;
}

.sidebar__divider {
  margin: 7px 18px 9px;
  border-color: var(--sidebar-divider) !important;
}

.sidebar__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 12px 14px;
  padding: 24px 18px;
  color: var(--sidebar-muted);
  text-align: center;
  border: 1px dashed rgba(255, 255, 255, 0.09);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.025);
}

.sidebar__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-bottom: 11px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
}

.sidebar__empty-icon .v-icon {
  color: var(--sidebar-muted) !important;
}

.sidebar__empty strong {
  color: var(--sidebar-text);
  font-size: 0.8125rem;
}

.sidebar__empty small {
  margin-top: 4px;
  color: var(--sidebar-subtle);
  font-size: 0.75rem;
}

.sidebar__footer {
  flex: 0 0 auto;
  min-width: 268px;
  padding-bottom: 5px;
  padding-bottom: max(5px, env(safe-area-inset-bottom, 0px));
  background: linear-gradient(to bottom, transparent, rgba(12, 13, 15, 0.14) 22%);
}

.sidebar__footer .sidebar__divider {
  margin-top: 0;
}

::v-deep .sidebar__utility-list.v-list {
  padding-bottom: 4px;
}

::v-deep .sidebar__utility-item.v-list-item {
  color: var(--sidebar-muted) !important;
}

::v-deep .sidebar__utility-item .sidebar__item-icon {
  background: rgba(255, 255, 255, 0.04);
}

.sidebar__utility-arrow {
  color: var(--sidebar-subtle) !important;
}

.sidebar__brand-mark:focus-visible,
.sidebar ::v-deep .v-btn:focus-visible,
::v-deep .sidebar__item.v-list-item:focus-visible,
::v-deep .sidebar__group > .v-list-group__header:focus-visible,
::v-deep .sidebar__subitem.v-list-item:focus-visible {
  outline: 2px solid var(--sidebar-accent) !important;
  outline-offset: -2px;
  box-shadow: 0 0 0 3px var(--sidebar-accent-ring) !important;
}

.sidebar--mobile {
  border-radius: 0 26px 26px 0;
  box-shadow: 24px 0 72px rgba(0, 0, 0, 0.38);
}

.sidebar--mobile .sidebar__header {
  min-height: 82px;
}

.sidebar--mobile ::v-deep .sidebar__item.v-list-item,
.sidebar--mobile ::v-deep .sidebar__group > .v-list-group__header {
  min-height: 54px;
}

.sidebar--mobile ::v-deep .sidebar__subitem.v-list-item {
  min-height: 48px;
}

@media (max-width: 359px) {
  .sidebar__header {
    padding-right: 10px;
    padding-left: 10px;
  }

  ::v-deep .sidebar__list.v-list {
    padding-right: 7px;
    padding-left: 7px;
  }
}

@supports (color: color-mix(in srgb, white 50%, black)) {
  .sidebar {
    --sidebar-accent-soft: color-mix(in srgb, var(--sidebar-accent) 14%, transparent);
    --sidebar-accent-ring: color-mix(in srgb, var(--sidebar-accent) 20%, transparent);
  }
}

@media (prefers-contrast: more) {
  .sidebar {
    --sidebar-border: rgba(255, 255, 255, 0.2);
    --sidebar-divider: rgba(255, 255, 255, 0.16);
    --sidebar-muted: rgba(255, 255, 255, 0.78);
    --sidebar-subtle: rgba(255, 255, 255, 0.62);
  }

  ::v-deep .sidebar__item--active.v-list-item,
  ::v-deep .sidebar__group--active > .v-list-group__header {
    border-color: var(--sidebar-accent);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar,
  .sidebar *,
  .sidebar *::before,
  .sidebar *::after {
    scroll-behavior: auto !important;
    transition-duration: 1ms !important;
    transition-delay: 0ms !important;
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
